import {
	alpha,
	Box,
	ButtonBase,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import {
	ChevronRight,
	MessageCircle,
	Plus,
	Sparkles,
	Target,
} from "lucide-react";
import type { FC } from "react";
import { ExampleGoalList } from "web_myplann/Goals/Components/ExampleGoalList";
import {
	CTA_BLUE,
	CTA_GRADIENT,
	CTA_INDIGO,
} from "web_myplann/Goals/Types/constants";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

export const GoalsEmptyState: FC = () => {
	const { palette } = useTheme();
	const { t } = useResource();

	return (
		<Stack>
			<Box
				sx={{
					position: "relative",
					background: CTA_GRADIENT,
					px: 1.6,
					pt: "3.5rem",
					pb: 2,
				}}
			>
				<Box
					sx={{
						position: "absolute",
						inset: 0,
						overflow: "hidden",
						pointerEvents: "none",
					}}
				>
					<Sparkles
						size={64}
						style={{
							position: "absolute",
							top: 25,
							right: 24,
							color: alpha(palette.common.white, 0.05),
						}}
					/>
					<Target
						size={48}
						style={{
							position: "absolute",
							bottom: 16,
							left: 16,
							color: alpha(palette.common.white, 0.05),
						}}
					/>
				</Box>

				<Stack
					alignItems="center"
					textAlign="center"
					mb={2.5}
					mt={3}
					position="relative"
				>
					<Typography
						fontSize={20}
						fontWeight={700}
						color={palette.common.white}
						mb={1}
					>
						{t(Resources.Goals.EmptyState.title)}
					</Typography>
					<Typography
						fontSize={12}
						color={alpha(palette.common.white, 0.7)}
						lineHeight={1.5}
						maxWidth={260}
					>
						{t(Resources.Goals.EmptyState.description)}
					</Typography>
				</Stack>

				<Stack gap={1} mt={5} position="relative">
					<ButtonBase sx={{ width: "100%", borderRadius: "12px" }}>
						<Stack
							direction="row"
							alignItems="center"
							gap={1.2}
							sx={{
								width: "100%",
								bgcolor: alpha(palette.common.white, 0.15),
								backdropFilter: "blur(8px)",
								borderRadius: "12px",
								px: 1.6,
								py: 1.2,
							}}
						>
							<Box
								sx={{
									width: 40,
									height: 40,
									borderRadius: "12px",
									bgcolor: CTA_BLUE,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									flexShrink: 0,
								}}
							>
								<Plus size={20} color={palette.common.white} />
							</Box>
							<Typography
								fontSize={14}
								fontWeight={500}
								color={palette.common.white}
							>
								{t(Resources.Goals.EmptyState.addGoalButton)}
							</Typography>
							<ChevronRight
								size={16}
								color={alpha(palette.common.white, 0.5)}
								style={{ marginLeft: "auto" }}
							/>
						</Stack>
					</ButtonBase>

					<ButtonBase sx={{ width: "100%", borderRadius: "12px" }}>
						<Stack
							direction="row"
							alignItems="center"
							gap={1.2}
							sx={{
								width: "100%",
								bgcolor: alpha(palette.common.white, 0.15),
								backdropFilter: "blur(8px)",
								borderRadius: "12px",
								px: 1.6,
								py: 1.2,
							}}
						>
							<Box
								sx={{
									width: 40,
									height: 40,
									borderRadius: "12px",
									bgcolor: CTA_INDIGO,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									flexShrink: 0,
								}}
							>
								<MessageCircle size={20} color={palette.common.white} />
							</Box>
							<Typography
								fontSize={14}
								fontWeight={500}
								color={palette.common.white}
							>
								{t(Resources.Goals.EmptyState.getAdviceButton)}
							</Typography>
							<ChevronRight
								size={16}
								color={alpha(palette.common.white, 0.5)}
								style={{ marginLeft: "auto" }}
							/>
						</Stack>
					</ButtonBase>
				</Stack>
			</Box>

			<Stack mt={0.8} bgcolor={palette.grey[50]}>
				<Typography
					fontSize={12}
					fontWeight={600}
					color={palette.grey[500]}
					textTransform="uppercase"
					letterSpacing={0.8}
					px={1.6}
					py={1}
				>
					{t(Resources.Goals.EmptyState.examplesTitle)}
				</Typography>

				<ExampleGoalList />
			</Stack>
		</Stack>
	);
};
